import { trackEvent, TRACK_EVENT } from '~/utils/track';
import { round } from 'lodash-es';

const transform = (time: number) => round(+time.toFixed(2), 2);

export class PerformanceDetector {
  public observer: PerformanceObserver | null = null;
  private OSS_WHITE_LIST = [
    'youyan3d.com',
    'rendora.ai',
    'fe-assets-cdn.rendora.ai',
    'oss.rendora.ai',
    'fe.rendora.ai',
    'usermedia.rendora.ai',
    'google.com',
    'googleads.g.doubleclick.net',
    'fe-assets-cdn.youyan3d.com',
    'official-site.youyan3d.com',
    'official-site.youyan.xyz',
    'media.youyan3d.com',
    'media.youyan.xyz',
    'media.xmov.ai',
  ];

  videoReported = false;
  videoEntries: PerformanceResourceTiming[] = [];

  constructor() {
    if (import.meta.client && 'PerformanceObserver' in window) {
      // 监听资源链接，当出现异常资源链接时，console 到控制台
      this.observer = new PerformanceObserver(this.performanceObserverCallback);
      this.observer.observe({ entryTypes: ['resource'] });
      // 用户的网络基准测试
      this.doImageBenchmark();
      this.doVideoBenchmark();
    }
  }

  performanceObserverCallback = (list: PerformanceObserverEntryList) => {
    const entries = list.getEntries() as PerformanceResourceTiming[];
    entries.forEach(entry => {
      if (entry.deliveryType === 'cache' || entry.transferSize === 0) return;
      if (['img', 'video', 'audio'].includes(entry.initiatorType)) {
        const isMatchSafeDomain = this.OSS_WHITE_LIST.some(domain => entry.name.includes(domain));
        if (!isMatchSafeDomain) {
          console.error('[Non-OSS resources detected]', entry.name);
          return;
        }
        switch (entry.initiatorType) {
          case 'img':
            this.analyzeImg(entry);
            break;
          case 'video':
            this.analyzeVideo(entry);
            break;
        }
      }
    });
  };

  analyzeVideo = (entry: PerformanceResourceTiming) => {
    if (entry.name.includes('benchmark.mp4') && !this.videoReported) {
      this.videoEntries.push(entry);
    }
  };

  analyzeImg = (entry: PerformanceResourceTiming) => {
    if (entry.name.includes('benchmark.png')) {
      const duration = transform(entry.duration);
      const dnsLookupTime = transform(entry.domainLookupEnd - entry.domainLookupStart);
      const connectTime = transform(entry.connectEnd - entry.connectStart);
      const sslTime = transform(entry.connectEnd - entry.secureConnectionStart);
      const responseTime = transform(entry.responseStart - entry.requestStart);
      const transferTime = transform(entry.responseEnd - entry.responseStart);
      const transferSize = transform(entry.transferSize / 1024); // 单位 kb
      const transferSpeed = transform(transferSize / (transferTime / 1000)); // 单位 kb/s
      const payload = {
        duration: duration,
        transferSize: transferSize,
        responseTime: responseTime,
        transferTime: transferTime,
        transferSpeed: transferSpeed,
        dns: dnsLookupTime,
        connectTime: connectTime,
        sslTime: sslTime,
      };
      trackEvent(TRACK_EVENT.IMAGE_BENCHMARK, payload);
    }
  };

  doImageBenchmark() {
    const imgSrc = getFeUrl('/assets/benchmark.png');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.loading = 'eager';
    img.src = imgSrc;
  }

  doVideoBenchmark() {
    const videoSrc = getFeUrl('/assets/benchmark.mp4');
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.src = videoSrc;
    video.onloadedmetadata = () => {
      this.videoReported = true;
      if (!this.videoEntries.length) return;
      const aggregate = this.videoEntries.reduce(
        (acc, entry) => {
          const duration = entry.duration;
          const dnsLookupTime = entry.domainLookupEnd - entry.domainLookupStart;
          const connectTime = entry.connectEnd - entry.connectStart;
          const sslTime = entry.connectEnd - entry.secureConnectionStart;
          const responseTime = entry.responseStart - entry.requestStart;
          const transferTime = entry.responseEnd - entry.responseStart;
          const transferSize = entry.transferSize / 1024; // 单位 kb
          const transferSpeed = transferSize / (transferTime / 1000); // 单位 kb/s
          acc.duration += duration;
          acc.transferSize += transferSize;
          acc.responseTime += responseTime;
          acc.transferTime += transferTime;
          acc.transferSpeed += transferSpeed;
          acc.dns += dnsLookupTime;
          acc.connectTime += connectTime;
          acc.sslTime += sslTime;
          return acc;
        },
        {
          duration: 0,
          transferSize: 0,
          responseTime: 0,
          transferTime: 0,
          transferSpeed: 0,
          dns: 0,
          connectTime: 0,
          sslTime: 0,
        }
      );

      trackEvent(TRACK_EVENT.VIDEO_BENCHMARK, {
        duration: transform(aggregate.duration),
        transferSize: transform(aggregate.transferSize),
        responseTime: transform(aggregate.responseTime),
        transferTime: transform(aggregate.transferTime),
        transferSpeed: transform(aggregate.transferSpeed / this.videoEntries.length),
        dns: transform(aggregate.dns),
        connectTime: transform(aggregate.connectTime),
        sslTime: transform(aggregate.sslTime),
      });
    };
    video.load();
  }
}
